import { EquipmentTypeKeysByQestId } from 'Components/Equipment/equipmentTypes';
import { actions } from 'Data/ReduxViewModels/EquipmentInterfacing';
import { FormatTypes } from 'Lib/Format';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ListFieldSection } from '../../Components/Layout/FieldSection/ListFieldSection';
import { CreatableDropdown, Field, Message, NumericInput, RadioGroup } from '../Core';
import { InterfaceConnectionButtons } from '../Equipment/Interfacing/InterfaceConnectionButtons';

interface IDriverTestingSectionProps {
  driver: any;
}

const DriverTestingSection: React.FC<IDriverTestingSectionProps> = ({ driver }) => {
  const dispatch = useDispatch();
  const [baudRate, setBaudRate] = useState(9600);
  const [parity, setParity] = useState<ParityType>('none');
  const [dataBits, setDataBits] = useState(8);
  const [stopBits, setStopBits] = useState(1);
  /** The generated unique equipmentId is used to test so that the connection status will be specific for each individual equipment driver */
  const equipmentId = `testEquipmentIdForDriver${driver?.Id}`;
  const equipmentType = driver?.Data?.EquipmentType?.Name ?? 'equipment item';
  const equipmentModel = {
    Id: equipmentId,
    Code: 'Test Equipment',
    Driver: driver,
    PortSettings: {
      BaudRate: baudRate,
      Parity: parity,
      DataBits: dataBits,
      StopBits: stopBits,
      IsValidPortSettings: true,
    },
  };

  useEffect(() => {
    return () => {
      dispatch(actions.cleanupEquipment({ equipmentId }));
    };
  }, [dispatch, equipmentId]);

  const [readingFromEquipment, setReadingFromEquipment] = useState<number>();
  const isConnected = useSelector((s) => s.equipmentInterfacing.equipment[equipmentId]?.isConnected ?? false);
  return (
    <>
      <Message info>
        The settings below are for testing only and are not saved against this driver. These settings should be
        re-entered against each relevant {equipmentType} in the equipment register.
      </Message>
      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          label="Parity"
          value={parity}
          onChange={(event: any) => setParity(event.data.value)}
          options={[
            { label: 'None', value: 'none' },
            { label: 'Even', value: 'even' },
            { label: 'Odd', value: 'odd' },
          ]}
          disabled={isConnected}
        />
        <Field
          input={<CreatableDropdown />}
          label="Baud Rate"
          value={baudRate}
          onChange={(event: any) => setBaudRate(parseInt(event.data.value?.value))}
          options={[
            { label: '9600', value: 9600 },
            { label: '19200', value: 19200 },
            { label: '38400', value: 38400 },
            { label: '57600', value: 57600 },
          ]}
          formatCreateLabel={(label: string) => `Other: ${label}`}
          disabled={isConnected}
        />
        <Field
          input={<RadioGroup />}
          label="Data Bits"
          value={dataBits}
          onChange={(event: any) => setDataBits(event.data.value)}
          options={[
            { label: '7 bits', value: 7 },
            { label: '8 bits', value: 8 },
          ]}
          disabled={isConnected}
        />
        <Field
          input={<RadioGroup />}
          label="Stop Bits"
          value={stopBits}
          onChange={(event: any) => setStopBits(event.data.value)}
          options={[
            { label: '1 bit', value: 1 },
            { label: '2 bits', value: 2 },
          ]}
          disabled={isConnected}
        />
      </ListFieldSection>
      <InterfaceConnectionButtons
        equipment={equipmentModel}
        equipmentType={EquipmentTypeKeysByQestId[driver?.Data?.EquipmentType?.Id]}
      />
      <ListFieldSection>
        <Field
          format={{ type: FormatTypes.AsEntered }}
          input={<NumericInput />}
          label="Field for equipment interfacing"
          onChange={(event: any) => setReadingFromEquipment(event.data.value)}
          value={readingFromEquipment}
          additionalInformationDialogContent="Use this to test data entry for a field connected to an equipment item with the above driver and connection settings."
          measuredUsing={equipmentModel}
        />
      </ListFieldSection>
    </>
  );
};

export default DriverTestingSection;
export { DriverTestingSection };
